import apiClient from './client';

export enum NotificationType {
  SYSTEM = 'system',
  PAYMENT = 'payment',
  SUBSCRIPTION = 'subscription',
  PROMO = 'promo',
  REMINDER = 'reminder',
  ACHIEVEMENT = 'achievement',
}

export enum NotificationStatus {
  PENDING = 'pending',
  SCHEDULED = 'scheduled',
  SENT = 'sent',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum AudienceType {
  ALL = 'all',
  PREMIUM = 'premium',
  FREE = 'free',
  BY_PREFERENCES = 'by_preferences',
  BY_ACTIVITY = 'by_activity',
}

export interface ActivityFilters {
  days_inactive?: number;
  days_active?: number;
}

export interface AudienceFilters {
  audience_type: AudienceType;
  activity_filters?: ActivityFilters;
  notification_types?: string[];
}

export interface PreviewNotificationDto {
  title: string;
  body: string;
  type: NotificationType;
  data?: Record<string, any>;
  audience_filters?: AudienceFilters;
}

export interface PreviewNotificationResponse {
  estimated_recipients: number;
  estimated_devices: number;
  breakdown: {
    total_users: number;
    premium_users: number;
    free_users: number;
    users_with_preferences: number;
    active_users: number;
    inactive_users: number;
  };
  message: string;
}

export interface CreateNotificationDto {
  title: string;
  body: string;
  type: NotificationType;
  data?: Record<string, any>;
  audience_filters?: AudienceFilters;
  scheduled_at?: string;
  priority?: number;
  is_sponsored?: boolean;
  is_premium?: boolean;
}

export interface CreateNotificationResponse {
  id: string;
  status: NotificationStatus;
  estimated_recipients: number | null;
  scheduled_at: string | null;
  message: string;
}

export interface NotificationListItem {
  id: string;
  title: string;
  body: string;
  type: NotificationType;
  status: NotificationStatus;
  created_at: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_by: string | null;
  estimated_recipients: number | null;
  sent_count: number;
  opened_count: number;
  open_rate: number;
  is_sponsored: boolean;
  is_premium: boolean;
}

export interface ListNotificationsParams {
  page?: number;
  limit?: number;
  status?: NotificationStatus;
  type?: NotificationType;
  search?: string;
}

export interface ListNotificationsResponse {
  notifications: NotificationListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface NotificationStats {
  notification_id: string;
  title: string;
  type: string;
  status: string;
  created_at: string;
  scheduled_at: string | null;
  sent_at: string | null;
  estimated_recipients: number | null;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  failed_count: number;
  delivery_rate: number;
  open_rate: number;
  status_breakdown: {
    success: number;
    failed: number;
    invalid_token: number;
    device_not_registered: number;
  };
}

export interface NotificationAnalyticsResponse {
  notification: NotificationStats;
  logs_summary: {
    total_logs: number;
    by_status: Record<string, number>;
    by_date: Array<{
      date: string;
      sent: number;
      delivered: number;
      opened: number;
    }>;
  };
}

export interface NotificationAnalyticsParams {
  start_date?: string;
  end_date?: string;
  status?: NotificationStatus;
}

export const notificationsApi = {
  /**
   * Preview notification (dry run)
   */
  async preview(dto: PreviewNotificationDto): Promise<PreviewNotificationResponse> {
    const response = await apiClient.post<PreviewNotificationResponse>(
      '/admin/notifications/preview',
      dto
    );
    return response.data;
  },

  /**
   * Create notification
   */
  async create(dto: CreateNotificationDto): Promise<CreateNotificationResponse> {
    const response = await apiClient.post<CreateNotificationResponse>(
      '/admin/notifications',
      dto
    );
    return response.data;
  },

  /**
   * List notifications
   */
  async list(params?: ListNotificationsParams): Promise<ListNotificationsResponse> {
    const response = await apiClient.get<ListNotificationsResponse>(
      '/admin/notifications',
      { params }
    );
    return response.data;
  },

  /**
   * Get notification analytics
   */
  async getAnalytics(
    id: string,
    params?: NotificationAnalyticsParams
  ): Promise<NotificationAnalyticsResponse> {
    const response = await apiClient.get<NotificationAnalyticsResponse>(
      `/admin/notifications/${id}/analytics`,
      { params }
    );
    return response.data;
  },

  /**
   * Cancel scheduled notification
   */
  async cancel(id: string): Promise<void> {
    await apiClient.patch(`/admin/notifications/${id}/cancel`);
  },

  /**
   * Delete notification
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/admin/notifications/${id}`);
  },
};

